import { X } from 'lucide-react';
import { Button, ModalOverlay, StatusBadge } from './ui';
import { egp, type WithdrawalRequest } from '../api';

const STATUS_DISPLAY: Record<string, string> = {
  requested: 'pending',
  rejected: 'declined',
  paid: 'sent',
};

const SOURCE_LABEL: Record<string, string> = {
  clinic_weeks: 'Internships',
  consultations: 'Consultations',
  referrals: 'Referrals',
};

export type EarningsSummaryLine = {
  label: string;
  amountEgp: number;
};

export type EarningsWithdrawAction = {
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
};

export function WithdrawalRequestsList({
  requests,
  showSource = false,
}: {
  requests: WithdrawalRequest[];
  showSource?: boolean;
}) {
  if (requests.length === 0) {
    return (
      <p className="text-sm text-ink-soft">No withdrawal requests yet.</p>
    );
  }

  return (
    <div className="divide-y divide-line rounded-radius-sm border border-line overflow-hidden bg-bg">
      {requests.map(r => (
        <div key={r.id} className="px-3 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-ink">{egp(r.amountEgp)}</div>
            <div className="text-xs text-ink-soft mt-0.5">
              {showSource ? `${SOURCE_LABEL[r.source] ?? r.source} · ` : ''}
              {new Date(r.createdAt).toLocaleDateString('en-GB')}
              {r.status === 'paid' && r.paidAt
                ? ` · Sent ${new Date(r.paidAt).toLocaleDateString('en-GB')}`
                : ''}
            </div>
          </div>
          <StatusBadge status={STATUS_DISPLAY[r.status] ?? r.status} />
        </div>
      ))}
    </div>
  );
}

export function EarningsHistoryModal({
  open,
  onClose,
  summary,
  requests,
  loading,
  showSource = false,
  actions = [],
}: {
  open: boolean;
  onClose: () => void;
  summary: EarningsSummaryLine[];
  requests: WithdrawalRequest[];
  loading?: boolean;
  showSource?: boolean;
  actions?: EarningsWithdrawAction[];
}) {
  if (!open) return null;

  const pendingEgp = requests
    .filter(r => r.status === 'requested')
    .reduce((sum, r) => sum + r.amountEgp, 0);
  const sentEgp = requests
    .filter(r => r.status === 'paid')
    .reduce((sum, r) => sum + r.amountEgp, 0);
  const displaySummary = summary.map(line => {
    if (line.label === 'Pending' && !loading) return { ...line, amountEgp: pendingEgp };
    if (line.label === 'Sent' && !loading) return { ...line, amountEgp: sentEgp };
    return line;
  });

  return (
    <ModalOverlay onBackdropClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="earnings-history-title"
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] overflow-y-auto p-5 sm:p-6"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 id="earnings-history-title" className="font-heading text-lg font-bold text-ink">
              Payment history
            </h2>
            <p className="text-sm text-ink-soft mt-0.5">Profit and withdrawals</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-faint hover:text-ink p-1 rounded-lg hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-5">
          {displaySummary.map(line => (
            <div key={line.label} className="rounded-radius-sm border border-line bg-bg p-3">
              <div className="text-[11px] uppercase tracking-wide text-ink-soft">{line.label}</div>
              <div className="font-heading text-lg font-bold text-ink mt-0.5">{egp(line.amountEgp)}</div>
            </div>
          ))}
        </div>

        {actions.length > 0 && (
          <div className="space-y-2 mb-5">
            {actions.map(action => (
              <Button
                key={action.label}
                className="w-full"
                onClick={action.onClick}
                disabled={action.disabled}
                loading={action.loading}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}

        <div className="text-xs font-medium text-ink-soft uppercase tracking-wide mb-2">Withdrawals</div>
        {loading ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : (
          <WithdrawalRequestsList requests={requests} showSource={showSource} />
        )}
      </div>
    </ModalOverlay>
  );
}
